'use client'

import { usePostCreationStore } from '@/lib/stores/postCreationStore';
import { NewsPost } from '@/lib/types/posts';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Typography from '@tiptap/extension-typography';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { 
  Bold, 
  Italic, 
  Link as LinkIcon, 
  List, 
  ListOrdered,
  ExternalLink 
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function NewsEditor() {
  const { currentPost, updatePostField, updateMetadataField } = usePostCreationStore();
  const newsPost = currentPost as NewsPost;
  
  // Editor for the main news content
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Write your news content here...',
      }),
      Link.configure({
        openOnClick: false,
      }),
      Typography,
    ],
    content: newsPost.content || '',
    onUpdate: ({ editor }) => {
      updatePostField('content', editor.getHTML());
    },
    immediatelyRender: false,
  });
  
  const toggleBold = () => {
    editor?.chain().focus().toggleBold().run();
  };
  
  const toggleItalic = () => {
    editor?.chain().focus().toggleItalic().run();
  };
  
  const toggleBulletList = () => {
    editor?.chain().focus().toggleBulletList().run();
  };
  
  const toggleOrderedList = () => {
    editor?.chain().focus().toggleOrderedList().run();
  };
  
  const setLink = () => {
    const url = window.prompt('URL');
    
    if (url === null) {
      return;
    }
    
    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  
  const handleSourceChange = (value: string) => {
    updateMetadataField(['source'], value);
  };
  
  const handleImpactLevelChange = (value: 'high' | 'medium' | 'low') => {
    updateMetadataField(['impactLevel'], value);
  };
  
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Headline</Label>
        <Input
          id="title"
          placeholder="Enter news headline"
          value={newsPost.title || ''}
          onChange={(e) => updatePostField('title', e.target.value)}
          className="text-xl font-bold"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="excerpt">Summary</Label>
        <Input
          id="excerpt"
          placeholder="Brief summary of the news"
          value={newsPost.excerpt || ''}
          onChange={(e) => updatePostField('excerpt', e.target.value)}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="source">Source</Label>
          <Input
            id="source"
            placeholder="Source of the news"
            value={newsPost.source || ''}
            onChange={(e) => handleSourceChange(e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="impact-level">Impact Level</Label>
          <Select
            value={newsPost.impactLevel || 'medium'}
            onValueChange={(value) => handleImpactLevelChange(value as 'high' | 'medium' | 'low')}
          >
            <SelectTrigger id="impact-level">
              <SelectValue placeholder="Select impact level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High Impact</SelectItem>
              <SelectItem value="medium">Medium Impact</SelectItem>
              <SelectItem value="low">Low Impact</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Content</Label>
          <div className="flex items-center space-x-1 border rounded-md p-1">
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('bold') ? 'default' : 'ghost'}
              onClick={toggleBold}
              className="h-8 w-8 p-0"
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('italic') ? 'default' : 'ghost'}
              onClick={toggleItalic}
              className="h-8 w-8 p-0"
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('bulletList') ? 'default' : 'ghost'}
              onClick={toggleBulletList}
              className="h-8 w-8 p-0"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('orderedList') ? 'default' : 'ghost'}
              onClick={toggleOrderedList}
              className="h-8 w-8 p-0"
            >
              <ListOrdered className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={editor?.isActive('link') ? 'default' : 'ghost'}
              onClick={setLink}
              className="h-8 w-8 p-0"
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="border rounded-md p-4 min-h-[200px]">
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  );
} 